/** Public marketing constants for the PropBix landing page. */
import { TENANT_THEME_MODE } from './tenant-theme-ui.js';
export const LANDING_PAGE_ROUTE = Object.freeze({
    signup: '/onboarding/register',
    login: '/login',
    support: '/support',
    terms: '/terms',
    privacy: '/privacy',
});
export const LANDING_PAGE_SECTION = Object.freeze({
    product: 'product',
    platform: 'platform',
    setup: 'setup',
    trust: 'trust',
    faq: 'faq',
    day: 'day',
    capabilities: 'capabilities',
    gallery: 'gallery',
    personas: 'personas',
});
export const LANDING_THEME_STORAGE_KEY = 'propbix-landing-theme';
export const LANDING_THEME_MODE = TENANT_THEME_MODE;
/** Optional Vite env key for absolute tenant-app origin when landing is on a different host. */
export const LANDING_TENANT_APP_ORIGIN_ENV_KEY = 'VITE_TENANT_APP_ORIGIN';
export const LANDING_PAGE_UI = Object.freeze({
    brandName: 'PropBix',
    brandTagline: 'Property operations for building owners',
    skipToContent: 'Skip to content',
    navCta: 'Start setup',
    loginCta: 'Log in',
    themeToggleLight: 'Switch to light mode',
    themeToggleDark: 'Switch to dark mode',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    heroEyebrow: 'Owner operating system',
    heroTitle: 'The operating system for building owners.',
    heroSubtitle: 'Run the full property day from one desk—leases, rent follow-ups, maintenance, visitors, staff, and owner alerts—without rebuilding the morning from WhatsApp and spreadsheets.',
    heroPrimaryCta: 'Start setup',
    heroSecondaryCta: 'See a day',
    heroProof: 'Built for owners who operate buildings, not browse dashboards.',
    problemLabel: 'Monday morning',
    problemTitle: 'Your week is scattered. Your next action should not be.',
    problemSubtitle: 'Fourteen WhatsApp threads. Three complaints. Two maintenance calls. A renewal in email. Rent reminders in a sheet. Everything converges into one calm owner desk.',
    problemFromLabel: 'Scattered across',
    problemToLabel: 'Connected in PropBix',
    problemConvergeLabel: 'Everything converges into one owner desk',
    problemConvergeBody: 'Leases, rent follow-ups, maintenance, visitors, and owner alerts land on one desk—so Monday starts with next actions, not archaeology.',
    dayLabel: 'A day with PropBix',
    dayTitle: 'From morning walkthrough to end-of-day close.',
    daySubtitle: 'Seven moments that used to live in seven apps. Now they move through one desk.',
    productLabel: 'Workflows',
    productTitle: 'Owners buy workflows, not modules.',
    productSubtitle: 'Explore how work actually moves, from intake to archive, then start setup when you are ready.',
    showcaseHint: 'Choose a workflow',
    showcaseExampleLabel: 'Example owner desk',
    showcaseCta: 'Start setup',
    workflowLifecycleLabel: 'Workflow explorer',
    workflowBoardLabel: 'Operational view',
    capabilitiesLabel: 'Platform breadth',
    capabilitiesTitle: 'Everything you can run from the desk.',
    capabilitiesSubtitle: 'Browse by domain, then open a surface. This is the OS map—not a feature checklist.',
    capabilitiesSelectHint: 'Choose a surface',
    capabilitiesDomainHint: 'Choose a domain',
    capabilitiesSurfaceCountLabel: 'owner-desk surfaces across the OS',
    capabilitiesDomainSurfaceCountSuffix: 'surfaces',
    capabilitiesPreviewMetricLabel: 'At a glance',
    capabilitiesPreviewRowLabel: 'Desk row',
    dayPreviewBadge: 'Live desk',
    dayPreviewPrimaryLabel: 'Needs attention',
    dayPreviewUpdateLabel: 'Update',
    platformPrimaryActorLabel: 'Primary actor',
    platformSyncLabel: 'All in sync',
    platformSyncBody: 'Every surface shares the same operational data. No siloed modules, no export-and-import.',
    midCtaTitle: 'Ready to stop stitching the day together?',
    midCtaSubtitle: 'Start setup and map your first building when you are ready.',
    midCtaPrimary: 'Start setup',
    galleryLabel: 'Product surfaces',
    galleryTitle: 'Built for how property work actually looks.',
    gallerySubtitle: 'Real desk chrome. Operational data in context. Not a polished mockup that hides how the tool works.',
    platformLabel: 'Connected operations',
    platformTitle: 'One owner desk. Clear roles. Shared truth.',
    platformSubtitle: 'Staff verify visitors. Workers close maintenance. Residents are managed in the owner OS. AI assists on the desk. Admin runs the platform. Same data, no export hop.',
    platformHonesty: 'Owner-first OS. Resident self-service app is not part of this product.',
    platformHubLabel: 'Owner Command Center',
    personasLabel: 'Who runs work',
    personasTitle: 'One desk. Multiple roles.',
    personasSubtitle: 'PropBix is owner-first. Owners operate everything. Staff, workers, and visitors participate in specific, bounded ways.',
    setupLabel: 'How it works',
    setupTitle: 'Up and running in three steps.',
    setupSubtitle: 'Create your owner account, map what you own, then run day-to-day work from one desk.',
    setupCta: 'Start setup',
    trustLabel: 'Why PropBix',
    trustTitle: 'Owner-first. Workflow-driven. Portfolio-aware.',
    trustSubtitle: 'Not another ERP. Not a resident portal. An operating desk for people who run buildings.',
    faqLabel: 'FAQ',
    faqTitle: 'Questions owners ask before signup.',
    faqSubtitle: 'Straight answers. No invented metrics or compliance theater.',
    finalTitle: 'Run your portfolio from one desk.',
    finalSubtitle: 'Create your owner account, map buildings and units, then pull Monday’s work into one place.',
    finalPrimaryCta: 'Start setup',
    finalSecondaryCta: 'Log in',
    footerTagline: 'The operating system for building owners.',
    footerDayLabel: 'Day',
    footerProductLabel: 'Workflows',
    footerSetupLabel: 'How it works',
    footerPlatformLabel: 'Platform',
    footerSupportLabel: 'Support',
    footerTermsLabel: 'Terms',
    footerPrivacyLabel: 'Privacy',
    footerLoginLabel: 'Log in',
});
export const LANDING_PAGE_NAV_ITEMS = Object.freeze([
    { label: 'Day', href: `#${LANDING_PAGE_SECTION.day}` },
    { label: 'Workflows', href: `#${LANDING_PAGE_SECTION.product}` },
    { label: 'Platform', href: `#${LANDING_PAGE_SECTION.capabilities}` },
    { label: 'FAQ', href: `#${LANDING_PAGE_SECTION.faq}` },
]);
/** Visual chaos nodes for the problem storytelling band (not live product data). */
export const LANDING_PAGE_CHAOS_NODES = Object.freeze([
    { id: 'whatsapp', label: 'WhatsApp threads' },
    { id: 'excel', label: 'Excel sheets' },
    { id: 'email', label: 'Email chains' },
    { id: 'paper', label: 'Paper folders' },
    { id: 'calls', label: 'Maintenance calls' },
    { id: 'rent', label: 'Rent reminders' },
    { id: 'complaints', label: 'Complaints' },
    { id: 'pdfs', label: 'PDF agreements' },
]);
export const LANDING_PAGE_FRAGMENT_TOOLS = Object.freeze(LANDING_PAGE_CHAOS_NODES.map((node) => node.label));
export const LANDING_PAGE_CONNECTED_SURFACES = Object.freeze([
    'Buildings and units',
    'Residents and leases',
    'Agreement Studio',
    'Maintenance board',
    'Complaints',
    'Visitors',
    'Rent follow-ups',
    'Building staff',
    'Owner inbox',
    'Audit logs',
]);
/** Example Owner Command Center — illustrative only, not live customer data. */
export const LANDING_PAGE_COMMAND_CENTER = Object.freeze({
    exampleLabel: 'Example owner desk',
    deskTitle: 'Owner command center',
    deskSubtitle: 'Today · 3 buildings · 330 units',
    buildingSwitcher: 'Portfolio',
    buildings: [
        { name: 'Harbor Residences', units: 148, tone: 'active' },
        { name: 'Oakridge Towers', units: 112, tone: 'active' },
        { name: 'Cedar Court', units: 70, tone: 'quiet' },
    ],
    metrics: [
        { label: 'Occupancy', value: '94%', detail: '312 occupied' },
        { label: 'Rent collected', value: '86%', detail: 'March period' },
        { label: 'Open jobs', value: '11', detail: '3 need assign' },
        { label: 'Unread alerts', value: '7', detail: 'Owner inbox' },
    ],
    attentionTitle: 'Needs attention',
    attention: [
        { label: 'Harbor · 1204', status: 'Renewal due', tone: 'info' },
        { label: 'Oakridge · 0312', status: 'In repair', tone: 'warn' },
        { label: 'Cedar · B2 parking', status: 'Complaint open', tone: 'warn' },
        { label: 'Harbor · March rent', status: '4 pending', tone: 'info' },
    ],
    inboxTitle: 'Owner inbox',
    inbox: [
        { title: 'Lease renewal signed', meta: 'Harbor · 0808', time: '12m' },
        { title: 'Payment submitted for approval', meta: 'Oakridge · 1204', time: '41m' },
        { title: 'Maintenance completed', meta: 'MR-004761', time: '2h' },
    ],
    boardTitle: 'Maintenance',
    board: [
        { code: 'MR-4812', title: 'Lobby lighting', column: 'Assigned' },
        { code: 'MR-4790', title: 'Water seepage', column: 'In progress' },
        { code: 'MR-4761', title: 'AC service', column: 'Done' },
    ],
    rentTitle: 'Rent pulse',
    rentCollectedPct: 86,
    rentCaption: '286 of 330 units settled this period',
    buildingsChartTitle: 'Portfolio mix',
    floatCards: [
        {
            id: 'renewal',
            title: 'Lease renewal',
            body: 'Harbor · 1204 expires in 12 days',
            action: 'Open Agreement Studio',
            tone: 'info',
        },
        {
            id: 'dispatch',
            title: 'Needs assign',
            body: 'MR-004812 lobby lighting · Oakridge',
            action: 'Assign worker',
            tone: 'warn',
        },
    ],
});
/**
 * A-day-with-PropBix moments — illustrative only, not live customer data.
 * previewKind hints at the product surface shown in the preview panel.
 */
export const LANDING_PAGE_DAY_MOMENTS = Object.freeze([
    {
        id: 'morning-walkthrough',
        time: '8:14 AM',
        title: 'Morning portfolio check',
        detail: 'Owner opens the command center. Occupancy, open jobs, rent status, and inbox alerts — one read on the whole portfolio.',
        previewKind: 'command-center',
        previewTitle: 'Owner command center',
        previewLines: [
            '3 buildings · 330 units · 94% occupied',
            '11 open maintenance jobs · 3 need assign',
            '7 unread inbox alerts',
            'Rent collected: 86% this period',
        ],
    },
    {
        id: 'rent-reminder',
        time: '9:02 AM',
        title: 'Rent reminders sent',
        detail: 'Period bills are open. Preference-gated follow-ups go to overdue units. Owner approves a UPI payment from the desk.',
        previewKind: 'rent',
        previewTitle: 'Rent follow-ups',
        previewLines: [
            'Unit 0312 — overdue · reminder sent',
            'Unit 1204 — UPI pending · approve',
            'Unit 0808 — cheque clearing',
            '286 of 330 settled this period',
        ],
    },
    {
        id: 'maintenance-assign',
        time: '10:31 AM',
        title: 'Worker assigned to MR-004812',
        detail: 'Lobby lighting job in Oakridge assigned to an available worker from the maintenance board. Status updates hit the inbox.',
        previewKind: 'maintenance',
        previewTitle: 'Maintenance board',
        previewLines: [
            'MR-004812 · Lobby lighting → Assigned',
            'MW-000014 · Rajesh Kumar · available',
            'MR-004790 · Water seepage · In progress',
            'MR-004761 · AC service · Done',
        ],
    },
    {
        id: 'visitor-otp',
        time: '11:48 AM',
        title: 'Visitor verified at the desk',
        detail: 'Staff checks in a courier with Name + OTP. Owner created the pass earlier. No calls, full audit trail auto-archived.',
        previewKind: 'visitors',
        previewTitle: 'Visitor passes',
        previewLines: [
            'Courier · Tower A · OTP 8821',
            'Verified by Priya (front desk) 11:48',
            'Guest · Unit 1204 · checked in 10:14',
            'Vendor · lobby · checked out 11:02',
        ],
    },
    {
        id: 'agreement-signed',
        time: '2:17 PM',
        title: 'Lease renewal signed',
        detail: 'Harbor 0808 resident signs digitally. Signed PDF attached, renewal lineage preserved, owner inbox updated.',
        previewKind: 'agreements',
        previewTitle: 'Agreement Studio',
        previewLines: [
            'A-0808 renewal · signed · PDF ready',
            'Harbor · 12-month term · ₹22,000/mo',
            'A-1204 renewal · sent · awaiting sign',
            'A-0312 · draft · ready to send',
        ],
    },
    {
        id: 'complaint-resolved',
        time: '3:55 PM',
        title: 'Complaint closed with outcome',
        detail: 'Cedar Court noise complaint resolved. Outcome recorded, resident notified, trail archived with the building.',
        previewKind: 'complaints',
        previewTitle: 'Complaint center',
        previewLines: [
            'CMP-2026-000042 · Noise · Resolved',
            'Outcome recorded · resident notified',
            'CMP-2026-000041 · Parking · Open',
            'CMP-2026-000038 · Lobby access · Closed',
        ],
    },
    {
        id: 'inbox-pulse',
        time: '6:30 PM',
        title: 'End-of-day inbox check',
        detail: 'Three completed jobs, two signed agreements, one complaint resolved, two rent approvals. All in the owner inbox.',
        previewKind: 'inbox',
        previewTitle: 'Owner inbox',
        previewLines: [
            'Lease renewal signed · Harbor 0808 · 2:17 PM',
            'Complaint resolved · Cedar · 3:55 PM',
            'Maintenance done · MR-004761 · 4:08 PM',
            'Payment approved · Oakridge 1204 · 5:12 PM',
        ],
    },
]);
/** Capability explorer items — illustrative descriptions of real product surfaces. */
export const LANDING_PAGE_CAPABILITIES = Object.freeze([
    {
        id: 'buildings',
        label: 'Buildings',
        summary: 'Map your full property structure: buildings, wings, floors, and units in one place.',
        previewTitle: 'Building inventory',
        previewLines: [
            'Harbor Residences · 4 wings · 148 units',
            'Oakridge Towers · 3 wings · 112 units',
            'Cedar Court · 2 wings · 70 units',
            '330 total units across portfolio',
        ],
    },
    {
        id: 'units',
        label: 'Units',
        summary: 'Unit types, floor plans, furnishing status, rent targets, and occupancy for every flat.',
        previewTitle: 'Unit inventory',
        previewLines: [
            'Unit 1204 · 3BHK · furnished · ₹28,000',
            'Unit 0808 · 2BHK · semi · ₹22,000 · occupied',
            'Unit 0312 · 2BHK · unfurnished · vacant',
            '94% occupancy this period',
        ],
    },
    {
        id: 'residents',
        label: 'Residents',
        summary: 'Resident directory, onboarding, emergency contacts, and lease history tied to units.',
        previewTitle: 'Resident directory',
        previewLines: [
            '312 active residents · 3 buildings',
            'Onboarded · pending · unverified filters',
            'Unit assignment · lease terms per resident',
            'Emergency contacts · occupation records',
        ],
    },
    {
        id: 'staff',
        label: 'Building Staff',
        summary: 'Manage front desk and on-site staff with permission groups and wing location tags.',
        previewTitle: 'Building staff',
        previewLines: [
            'BS-000041 · Priya Nair · Front desk · Active',
            'BS-000038 · Rajan · Security · Active',
            'Permission group assigned per staff member',
            'Wing locations · login access flag',
        ],
    },
    {
        id: 'maintenance',
        label: 'Maintenance',
        summary: 'Owner-run kanban board: file, assign, track, and close every job with an audit trail.',
        previewTitle: 'Maintenance board',
        previewLines: [
            'MR-004812 · Lobby lighting · Assigned',
            'MR-004790 · Water seepage · In progress',
            'MR-004761 · AC service · Done',
            '11 open jobs · 3 need assign',
        ],
    },
    {
        id: 'workers',
        label: 'Workers',
        summary: 'Org-wide maintenance worker roster with job stats, documents, and assignment readiness.',
        previewTitle: 'Worker roster',
        previewLines: [
            'MW-000014 · Rajesh Kumar · 3 open jobs',
            'MW-000011 · Dinesh · 1 open job',
            'Worker documents · identity on file',
            'Pending / open / completed job stats',
        ],
    },
    {
        id: 'visitors',
        label: 'Visitors',
        summary: 'Owner creates passes. Staff verify with Name + OTP. Audit trail retained automatically.',
        previewTitle: 'Visitor passes',
        previewLines: [
            'Courier · Tower A · OTP 8821',
            'Guest · Unit 1204 · checked in 10:14',
            'Vendor · lobby · checked out 11:02',
            'Full pass history archived',
        ],
    },
    {
        id: 'agreements',
        label: 'Agreements',
        summary: 'Agreement Studio for templates, custom variables, jurisdiction drafts, and in-product signing.',
        previewTitle: 'Agreement Studio',
        previewLines: [
            'A-0808 · renewal · signed PDF ready',
            'A-1204 · renewal · sent · awaiting sign',
            'Custom variables · jurisdiction templates',
            'Renewal lineage preserved on every agreement',
        ],
    },
    {
        id: 'rent',
        label: 'Rent',
        summary: 'Period bills, offline collection tracking, and owner approval for cash, UPI, transfer, and cheque.',
        previewTitle: 'Rent management',
        previewLines: [
            '286 of 330 units settled this period',
            'Unit 0312 overdue · reminder sent',
            'Unit 1204 · UPI pending · approve',
            'No payment gateway — offline collection only',
        ],
    },
    {
        id: 'complaints',
        label: 'Complaints',
        summary: 'Owner files on behalf of residents. Categorize, track, and close with outcome and history.',
        previewTitle: 'Complaint center',
        previewLines: [
            'CMP-2026-000042 · Noise after hours · High',
            'CMP-2026-000041 · Parking dispute · Open',
            'CMP-2026-000038 · Lobby access · Closed',
            'Outcome recorded on every close',
        ],
    },
    {
        id: 'documents',
        label: 'Documents',
        summary: 'Building, unit, and resident documents organized, linked, and accessible from the desk.',
        previewTitle: 'Document library',
        previewLines: [
            'Maintenance → Rajesh ID proof on file',
            'Unit 1204 → NOC attached',
            'Harbor Residences → building rules PDF',
            'Resident shares · unit links',
        ],
    },
    {
        id: 'facilities',
        label: 'Facilities',
        summary: 'Amenity booking with slot rules, capacity limits, and resident quota management.',
        previewTitle: 'Facilities',
        previewLines: [
            'Clubhouse · 2 slots booked today',
            'Gym · open · no limit set',
            'Slot rules · capacity per session',
            'Booking history per building',
        ],
    },
    {
        id: 'notifications',
        label: 'Owner Inbox',
        summary: 'Every outcome across all surfaces lands in the owner inbox with building badges and deep links.',
        previewTitle: 'Owner inbox',
        previewLines: [
            'Lease renewal signed · Harbor 0808',
            'Payment submitted · Oakridge 1204',
            'Maintenance completed · MR-004761',
            'Multi-building filter · mark read',
        ],
    },
    {
        id: 'audit',
        label: 'Audit Logs',
        summary: 'Append-only activity trail for property operations — who did what, when, and on which building.',
        previewTitle: 'Audit logs',
        previewLines: [
            'Unit deleted · Harbor · 11:30 AM',
            'Agreement v3 published',
            'Worker assigned · MR-004812',
            'Building setting updated · Cedar',
        ],
    },
    {
        id: 'ai',
        label: 'AI Assistance',
        summary: 'AI agreement generation from jurisdiction templates, with BYOK or subscription credits.',
        previewTitle: 'AI Agreement Studio',
        previewLines: [
            'Full draft · Maharashtra jurisdiction',
            'Custom variables prefilled',
            'BYOK OpenAI / Anthropic / Gemini',
            'Token usage tracked per generation',
        ],
    },
    {
        id: 'permissions',
        label: 'Permissions',
        summary: 'Permission groups control what building staff can access, per building and per role.',
        previewTitle: 'Permission groups',
        previewLines: [
            'Front desk · visitor verify + directory read',
            'Maintenance desk · job update access',
            'Custom groups · owner-defined',
            'Assigned per staff member',
        ],
    },
]);
/** Domain rails for the capability explorer — Stitch-validated left-rail IA. */
export const LANDING_PAGE_CAPABILITY_DOMAINS = Object.freeze([
    {
        id: 'property',
        label: 'Property & assets',
        description: 'Buildings, units, facilities, and documents in one inventory.',
        capabilityIds: ['buildings', 'units', 'facilities', 'documents'],
    },
    {
        id: 'people',
        label: 'People & access',
        description: 'Residents, staff, visitors, and permission groups the owner controls.',
        capabilityIds: ['residents', 'staff', 'visitors', 'permissions'],
    },
    {
        id: 'money',
        label: 'Money & agreements',
        description: 'Offline rent collection, Agreement Studio, and AI drafting on the desk.',
        capabilityIds: ['rent', 'agreements', 'ai'],
    },
    {
        id: 'operations',
        label: 'Operations',
        description: 'Maintenance, workers, complaints, owner inbox, and audit trail.',
        capabilityIds: ['maintenance', 'workers', 'complaints', 'notifications', 'audit'],
    },
]);
/** Product gallery surfaces — illustrative desk previews, not live customer data. */
export const LANDING_PAGE_GALLERY = Object.freeze([
    {
        id: 'command-center',
        title: 'Owner Command Center',
        caption: 'Portfolio metrics, needs-attention queue, maintenance board, and rent pulse — all in one view.',
        accentTone: 'teal',
        mockRows: [
            { label: 'Occupancy', value: '94%', tone: 'ok' },
            { label: 'Rent collected', value: '86%', tone: 'info' },
            { label: 'Open jobs', value: '11', tone: 'warn' },
            { label: 'Unread alerts', value: '7', tone: 'info' },
        ],
    },
    {
        id: 'maintenance-board',
        title: 'Maintenance Board',
        caption: 'Kanban with intake, assignment, in-progress, and close-out — auto-coded and auditable.',
        accentTone: 'amber',
        mockRows: [
            { label: 'MR-004812 Lobby lighting', value: 'Assigned', tone: 'info' },
            { label: 'MR-004790 Water seepage', value: 'In progress', tone: 'warn' },
            { label: 'MR-004761 AC service', value: 'Done', tone: 'ok' },
            { label: '3 jobs need assign', value: '·', tone: 'warn' },
        ],
    },
    {
        id: 'agreement-studio',
        title: 'Agreement Studio',
        caption: 'Document-first editor with custom variables, jurisdiction templates, and built-in digital signing.',
        accentTone: 'teal',
        mockRows: [
            { label: 'A-0808 Harbor renewal', value: 'Signed', tone: 'ok' },
            { label: 'A-1204 renewal', value: 'Sent', tone: 'info' },
            { label: 'A-0312 new lease', value: 'Draft', tone: 'neutral' },
            { label: 'Template v3 published', value: '·', tone: 'info' },
        ],
    },
    {
        id: 'resident-directory',
        title: 'Resident Directory',
        caption: 'Onboarded residents, unit assignments, lease terms, and emergency contacts in one searchable table.',
        accentTone: 'neutral',
        mockRows: [
            { label: 'Arjun Sharma · Unit 1204', value: 'Active lease', tone: 'ok' },
            { label: 'Meera Pillai · Unit 0808', value: 'Renewal due', tone: 'warn' },
            { label: 'Vikram Nair · Unit 0312', value: 'Pending invite', tone: 'info' },
            { label: '312 total · 94% onboarded', value: '·', tone: 'info' },
        ],
    },
    {
        id: 'rent-pulse',
        title: 'Rent Pulse',
        caption: 'Period bills, offline collection tracking, pending approvals, and overdue reminders.',
        accentTone: 'amber',
        mockRows: [
            { label: 'Unit 0312 overdue', value: 'Remind', tone: 'warn' },
            { label: 'Unit 1204 UPI', value: 'Approve', tone: 'info' },
            { label: 'Unit 0808 cheque', value: 'Clearing', tone: 'neutral' },
            { label: '286 / 330 settled', value: '86%', tone: 'ok' },
        ],
    },
    {
        id: 'complaint-center',
        title: 'Complaint Center',
        caption: 'Owner-filed complaints with category, priority, notes, outcome, and full close-out trail.',
        accentTone: 'neutral',
        mockRows: [
            { label: 'CMP-2026-000042 Noise', value: 'High · Open', tone: 'warn' },
            { label: 'CMP-2026-000041 Parking', value: 'Medium · Open', tone: 'info' },
            { label: 'CMP-2026-000038 Lobby', value: 'Resolved', tone: 'ok' },
            { label: '2 open · 1 resolved today', value: '·', tone: 'info' },
        ],
    },
    {
        id: 'visitor-passes',
        title: 'Visitor Passes',
        caption: 'Owner-created passes with Name + OTP verification at the desk — no calls, full audit trail.',
        accentTone: 'teal',
        mockRows: [
            { label: 'Courier Tower A', value: 'OTP ready', tone: 'info' },
            { label: 'Guest Unit 1204', value: 'Checked in', tone: 'ok' },
            { label: 'Vendor lobby', value: 'Checked out', tone: 'ok' },
            { label: '3 passes today', value: '·', tone: 'info' },
        ],
    },
    {
        id: 'owner-inbox',
        title: 'Owner Inbox',
        caption: 'Every outcome from every surface lands here — grouped, building-filtered, and deep-linked.',
        accentTone: 'teal',
        mockRows: [
            { label: 'Lease renewal signed', value: 'Harbor 0808 · 2:17 PM', tone: 'ok' },
            { label: 'Payment submitted', value: 'Oakridge 1204 · 5:12 PM', tone: 'info' },
            { label: 'Complaint resolved', value: 'Cedar · 3:55 PM', tone: 'ok' },
            { label: 'Maintenance done', value: 'MR-004761 · 4:08 PM', tone: 'ok' },
        ],
    },
]);
/** Honest ecosystem nodes — owner OS breadth (no resident self-service app). */
export const LANDING_PAGE_ECOSYSTEM_NODES = Object.freeze([
    { id: 'portfolio', label: 'Multi-building portfolio', ring: 1 },
    { id: 'structure', label: 'Buildings / wings / units', ring: 1 },
    { id: 'residents', label: 'Residents', ring: 1 },
    { id: 'staff', label: 'Building staff', ring: 1 },
    { id: 'maintenance', label: 'Maintenance', ring: 2 },
    { id: 'complaints', label: 'Complaints', ring: 2 },
    { id: 'visitors', label: 'Visitors', ring: 2 },
    { id: 'agreements', label: 'Agreement Studio', ring: 2 },
    { id: 'rent', label: 'Offline rent', ring: 2 },
    { id: 'documents', label: 'Documents', ring: 2 },
    { id: 'facilities', label: 'Facilities & parking', ring: 2 },
    { id: 'inbox', label: 'Owner inbox', ring: 1 },
    { id: 'audit', label: 'Audit logs', ring: 1 },
    { id: 'ai', label: 'AI assistance', ring: 1 },
    { id: 'admin', label: 'Platform admin', ring: 2 },
]);
/** Connected ecosystem roles — how each actor participates in the owner OS. */
export const LANDING_PAGE_ECOSYSTEM_ROLES = Object.freeze([
    {
        id: 'owner',
        label: 'Building Owner',
        description: 'Runs everything from the command center. Full access to all surfaces and the full portfolio.',
        surfaces: ['Command center', 'All 16 modules', 'Portfolio view', 'Owner inbox'],
        isHub: true,
        honesty: null,
    },
    {
        id: 'staff',
        label: 'Building Staff',
        description: 'Front desk and on-site operations. Permission-gated access assigned per role by the owner.',
        surfaces: ['Visitor verify (Name + OTP)', 'Maintenance view', 'Resident directory read'],
        isHub: false,
        honesty: null,
    },
    {
        id: 'worker',
        label: 'Maintenance Worker',
        description: 'Org-wide workforce. Jobs dispatched from the maintenance board by the owner.',
        surfaces: ['Job assignment', 'Worker profile', 'Documents on file'],
        isHub: false,
        honesty: null,
    },
    {
        id: 'visitor',
        label: 'Visitor',
        description: 'Verified at the desk with Name + OTP. No app. No login. Audit trail auto-retained.',
        surfaces: ['Visitor pass (OTP verify)', 'Check-in / check-out log'],
        isHub: false,
        honesty: null,
    },
    {
        id: 'resident',
        label: 'Resident (managed)',
        description: 'Owner manages residents, leases, and agreements on their behalf. No self-service portal.',
        surfaces: ['Resident profile', 'Agreement signing link', 'Onboarding via invite'],
        isHub: false,
        honesty: 'No resident self-service app. Owner manages everything.',
    },
    {
        id: 'admin',
        label: 'Platform Admin',
        description: 'PropBix platform team — provisions tenants, manages plans, and monitors health.',
        surfaces: ['Tenant provisioning', 'Feature flags', 'Billing observability'],
        isHub: false,
        honesty: null,
    },
]);
/** Personas band — who runs work in the owner operating system. */
export const LANDING_PAGE_PERSONAS = Object.freeze([
    {
        id: 'owner',
        role: 'Building Owner',
        description: 'Runs the whole desk. Every surface is built for owner decisions.',
        responsibilities: [
            'Portfolio and occupancy overview',
            'Lease renewals and agreement signing',
            'Maintenance triage and worker assignment',
            'Rent approval and follow-up',
            'Complaint lifecycle and close-out',
            'Visitor pass creation',
        ],
        surfaces: ['Command center', 'All modules', 'Inbox', 'Audit logs'],
        badge: 'Primary actor',
        badgeTone: 'accent',
        honesty: null,
    },
    {
        id: 'staff',
        role: 'Building Staff',
        description: 'Front desk and operations. Participates in bounded, owner-defined ways.',
        responsibilities: [
            'Verify visitor Name + OTP at entry desk',
            'View resident directory (read-only)',
            'Check maintenance job status',
        ],
        surfaces: ['Visitor passes', 'Directory view', 'Maintenance view'],
        badge: 'Permission-gated',
        badgeTone: 'neutral',
        honesty: null,
    },
    {
        id: 'worker',
        role: 'Maintenance Worker',
        description: 'Org-wide workforce managed from the maintenance board.',
        responsibilities: [
            'Assigned jobs by owner from the board',
            'Work history tracked per request',
            'Profile and documents stored on file',
        ],
        surfaces: ['Worker profile', 'Job board', 'Documents'],
        badge: 'Org-wide roster',
        badgeTone: 'neutral',
        honesty: null,
    },
    {
        id: 'resident',
        role: 'Resident (managed)',
        description: 'Owner manages residents, leases, and agreements on their behalf.',
        responsibilities: [
            'Directory record with lease and contact',
            'Agreement signing via email link',
            'Notified on key lease actions',
        ],
        surfaces: ['Resident profile', 'Agreement link', 'Onboarding'],
        badge: 'No portal',
        badgeTone: 'muted',
        honesty: 'There is no resident self-service app. The owner manages everything on behalf of residents.',
    },
    {
        id: 'admin',
        role: 'Platform Admin',
        description: 'PropBix platform team — provisions tenants and monitors health.',
        responsibilities: [
            'Tenant provisioning and lifecycle',
            'Feature flags and billing operations',
            'Platform health and audit monitoring',
        ],
        surfaces: ['Admin panel', 'Tenant list', 'Billing observability'],
        badge: 'Platform team',
        badgeTone: 'muted',
        honesty: null,
    },
]);
/** End-to-end owner workflows — illustrative example data only. */
export const LANDING_PAGE_WORKFLOWS = Object.freeze([
    {
        id: 'maintenance',
        label: 'Maintenance',
        title: 'The maintenance lifecycle',
        description: 'Owner-run boards keep intake, assignment, photos, and close-out in one trail.',
        steps: [
            { title: 'Issue filed', detail: 'Owner files on behalf of a resident' },
            { title: 'Ticket created', detail: 'Auto-coded and prioritized' },
            { title: 'Worker assigned', detail: 'Routed to an available worker' },
            { title: 'Photos uploaded', detail: 'Evidence stays on the request' },
            { title: 'Owner notified', detail: 'Status changes hit the inbox' },
            { title: 'Completed', detail: 'Signed off and archived' },
        ],
        boardTitle: 'Maintenance board',
        columns: [
            {
                title: 'In progress',
                cards: [
                    { title: 'Lobby lighting', meta: 'Harbor · MR-004812', badge: 'Assigned', tone: 'info' },
                    { title: 'Water seepage', meta: 'Oakridge · MR-004790', badge: 'Progress', tone: 'warn' },
                ],
            },
            {
                title: 'Review',
                cards: [
                    { title: 'AC service', meta: 'Cedar · MR-004761', badge: 'Done', tone: 'ok' },
                ],
            },
        ],
    },
    {
        id: 'rent',
        label: 'Rent',
        title: 'Offline collection, clear next action',
        description: 'Period bills, reminders, and owner approval for cash, UPI, transfer, and cheque. No payment gateway.',
        steps: [
            { title: 'Period bill open', detail: 'Invoice generated for the cycle' },
            { title: 'Reminder sent', detail: 'Preference-gated follow-up' },
            { title: 'Payment recorded', detail: 'Offline method captured' },
            { title: 'Owner approves', detail: 'Submitted payment reviewed' },
            { title: 'Receipt ready', detail: 'Collection confirmed' },
            { title: 'Ledger updated', detail: 'History stays with the unit' },
        ],
        boardTitle: 'Rent follow-ups',
        columns: [
            {
                title: 'Pending',
                cards: [
                    { title: 'Unit 1204 · UPI', meta: 'Harbor · approve', badge: 'Approve', tone: 'info' },
                    { title: 'Unit 0808 · cheque', meta: 'Harbor · clearing', badge: 'Pending', tone: 'warn' },
                ],
            },
            {
                title: 'Overdue',
                cards: [
                    { title: 'Unit 0312', meta: 'Oakridge · remind', badge: 'Overdue', tone: 'danger' },
                ],
            },
        ],
    },
    {
        id: 'agreements',
        label: 'Agreements',
        title: 'Agreement Studio to signed archive',
        description: 'Draft, send, sign, renew, and archive without chasing PDFs across email.',
        steps: [
            { title: 'Draft template', detail: 'Agreement Studio document' },
            { title: 'Send for signature', detail: 'Signing link to resident' },
            { title: 'Resident signs', detail: 'Typed or drawn signature' },
            { title: 'Owner notified', detail: 'Signed PDF attached' },
            { title: 'Renewal reminder', detail: 'Expiry sweeps ahead of term end' },
            { title: 'Archive', detail: 'Lineage preserved' },
        ],
        boardTitle: 'Signing trail',
        columns: [
            {
                title: 'In flight',
                cards: [
                    { title: 'A-1204 renewal', meta: 'Awaiting signature', badge: 'Sent', tone: 'info' },
                    { title: 'B-0312 new lease', meta: 'Ready to send', badge: 'Draft', tone: 'neutral' },
                ],
            },
            {
                title: 'Owner review',
                cards: [
                    { title: 'A-0808 amendment', meta: 'Needs review', badge: 'Review', tone: 'warn' },
                ],
            },
        ],
    },
    {
        id: 'complaints',
        label: 'Complaints',
        title: 'Complaint to resolution',
        description: 'Owner files on behalf of a resident, then tracks outcome through close.',
        steps: [
            { title: 'Complaint filed', detail: 'Owner creates the case' },
            { title: 'Categorized', detail: 'Priority and category set' },
            { title: 'In progress', detail: 'Notes and attachments added' },
            { title: 'Resolved', detail: 'Outcome recorded' },
            { title: 'Notified', detail: 'Owner inbox updated' },
            { title: 'Closed', detail: 'History retained for the building' },
        ],
        boardTitle: 'Complaint center',
        columns: [
            {
                title: 'Open',
                cards: [
                    { title: 'Noise after hours', meta: 'Cedar · high', badge: 'Open', tone: 'warn' },
                    { title: 'Parking dispute', meta: 'Harbor · medium', badge: 'Open', tone: 'info' },
                ],
            },
            {
                title: 'Resolved',
                cards: [
                    { title: 'Lobby access', meta: 'Oakridge · closed', badge: 'Done', tone: 'ok' },
                ],
            },
        ],
    },
    {
        id: 'visitors',
        label: 'Visitors',
        title: 'Visitor pass to audit trail',
        description: 'Owner creates the pass. Staff verify with Name + OTP. History stays auditable.',
        steps: [
            { title: 'Pass created', detail: 'Owner issues the visit' },
            { title: 'Pending arrival', detail: 'Window and guest details set' },
            { title: 'Staff verify', detail: 'Name + OTP at the desk' },
            { title: 'Checked in', detail: 'Entry recorded' },
            { title: 'Checked out', detail: 'Exit recorded when applicable' },
            { title: 'Audit trail', detail: 'Pass history retained' },
        ],
        boardTitle: 'Visitor passes',
        columns: [
            {
                title: 'Pending',
                cards: [
                    { title: 'Courier · Tower A', meta: 'OTP ready', badge: 'Pending', tone: 'info' },
                ],
            },
            {
                title: 'Verified today',
                cards: [
                    { title: 'Guest · Unit 1204', meta: 'Checked in 10:14', badge: 'Done', tone: 'ok' },
                    { title: 'Vendor · lobby', meta: 'Checked out 11:02', badge: 'Done', tone: 'ok' },
                ],
            },
        ],
    },
]);
/** @deprecated Prefer LANDING_PAGE_WORKFLOWS — kept for transitional imports. */
export const LANDING_PAGE_SHOWCASE = Object.freeze(LANDING_PAGE_WORKFLOWS.map((flow) => ({
    id: flow.id,
    label: flow.label,
    title: flow.title,
    description: flow.description,
    caption: flow.steps[0]?.detail ?? '',
    metrics: flow.steps.slice(0, 3).map((step) => ({ label: step.title, value: '·' })),
    rows: flow.columns.flatMap((column) => column.cards.map((card) => ({
        label: card.title,
        detail: card.meta,
        badge: card.badge,
        tone: card.tone,
    }))),
})));
export const LANDING_PAGE_WORKFLOW_STEPS = Object.freeze([
    {
        title: 'Create your owner account',
        description: 'Sign up once, then move straight into building setup with a guided first path.',
    },
    {
        title: 'Map buildings, wings, and units',
        description: 'Import inventory or build it in-app so occupancy and leases have a real home.',
    },
    {
        title: 'Invite work into the desk',
        description: 'Add residents, publish agreements, open maintenance, and let the inbox surface what needs you.',
    },
]);
export const LANDING_PAGE_TRUST_POINTS = Object.freeze([
    {
        title: 'Owner-first, always',
        description: 'Every surface is shaped for building-owner decisions. Resident self-service is a different product with a different philosophy. PropBix starts and ends with the owner desk.',
    },
    {
        title: 'Everything connected',
        description: 'Maintenance, rent, agreements, complaints, visitors, and inbox share the same operational data. Closing a job updates the board. Signing a lease updates the renewal timeline. Nothing is siloed.',
    },
    {
        title: 'Workflows, not modules',
        description: 'Work does not live in a module — it moves through a lifecycle. PropBix is built around how property operations actually flow, from intake to close-out, not feature cards on a pricing page.',
    },
    {
        title: 'Calm by design',
        description: 'The owner desk is built for focus. High-signal inbox, clear next actions, and no notification noise that does not require a decision. The goal is one calm read on the day, not endless dashboards.',
    },
]);
export const LANDING_PAGE_FAQ = Object.freeze([
    {
        question: 'Who is PropBix for?',
        answer: 'Building owners and hands-on property operators who need day-to-day control across units, residents, agreements, maintenance, complaints, visitors, staff, and rent. It is not a resident self-service app.',
    },
    {
        question: 'How long to first building?',
        answer: 'After you create an owner account, you can start mapping buildings, wings, and units right away. Import inventory or add it in-app as you go.',
    },
    {
        question: 'Can I run more than one building?',
        answer: 'Yes. PropBix supports multi-building portfolios so you can switch context while keeping org-wide signals visible.',
    },
    {
        question: 'How do agreements and signing work?',
        answer: 'Agreement Studio keeps templates, renewals, and signing follow-through with the lease so you are not chasing PDFs across email.',
    },
    {
        question: 'Does PropBix process rent payments online?',
        answer: 'Rent management digitizes offline collection (cash, UPI, bank transfer, and cheque) against period bills. There is no payment gateway in the product today.',
    },
    {
        question: 'Is there a resident mobile app?',
        answer: 'No. PropBix is owner-first. Owners manage residents, staff, and visitors from the owner desk. A resident self-service app is not part of this product.',
    },
    {
        question: 'When do I see pricing and billing?',
        answer: 'Plans and billing are chosen after you create your owner account. The landing page does not invent plan prices.',
    },
]);
/** @deprecated Prefer LANDING_PAGE_COMMAND_CENTER. */
export const LANDING_PAGE_PRODUCT_FRAME = Object.freeze({
    exampleLabel: LANDING_PAGE_COMMAND_CENTER.exampleLabel,
    deskTitle: LANDING_PAGE_COMMAND_CENTER.deskTitle,
    deskSubtitle: LANDING_PAGE_COMMAND_CENTER.deskSubtitle,
    needsAttention: LANDING_PAGE_COMMAND_CENTER.attentionTitle,
    rows: LANDING_PAGE_COMMAND_CENTER.attention.slice(0, 3).map((row) => ({
        label: row.label,
        status: row.status,
        tone: row.tone,
    })),
});
export const LANDING_PAGE_WORKFLOW_STEP_PREFIX = 'Step';
